/* eslint-disable i18next/no-literal-string */
import cn from 'classnames';
import { useState } from 'react';

import {
  type NestingProgress,
  useNestingStatusStore,
  type WorkerMeta,
} from '@/api/nesting';
import ReactPortal from '@/blocks/ReactPortal';

import styles from './NestingHud.module.css';

function WorkerProgressRow({
  workerIndex,
  progress,
  meta,
}: {
  workerIndex: number;
  progress: NestingProgress;
  meta?: WorkerMeta;
}) {
  const pct = Math.round(progress.value * 100);
  const done = pct >= 100;
  const sheetLabel = meta
    ? `${meta.lengthMM}x${meta.widthMM}`
    : `#${workerIndex}`;

  return (
    <div className={styles.workerRow}>
      <span className={styles.workerLabel}>{sheetLabel}</span>
      <div className={styles.progressBarOuter}>
        <div
          className={cn(styles.progressBarInner, { [styles.done]: done })}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={styles.progressPct}>{pct}%</span>
    </div>
  );
}

function GroupCard({ groupId }: { groupId: string }) {
  const status = useNestingStatusStore((s) => s.status[groupId]);
  const workerProgress = useNestingStatusStore(
    (s) => s.workerProgress[groupId]
  );
  const meta = useNestingStatusStore((s) => s.workerMeta[groupId]);

  const workers = workerProgress ? Object.entries(workerProgress) : [];

  return (
    <div className={styles.groupCard}>
      <div className={styles.groupHeader}>
        <span className={styles.groupId} title={groupId}>
          {groupId}
        </span>
        <span
          className={cn(styles.statusBadge, {
            [styles.statusRunning]: status === 'running',
            [styles.statusCompleted]: status === 'completed',
          })}
        >
          {status ?? 'idle'}
        </span>
      </div>

      {workers.map(([idx, progress]) => (
        <WorkerProgressRow
          key={idx}
          workerIndex={Number(idx)}
          progress={progress}
          meta={meta?.[Number(idx)]}
        />
      ))}
    </div>
  );
}

function PanelBody() {
  const groupIds = useNestingStatusStore((s) => Object.keys(s.status));

  if (groupIds.length === 0) {
    return <div className={styles.empty}>No active nesting workers</div>;
  }

  return (
    <>
      {groupIds.map((groupId) => (
        <GroupCard key={groupId} groupId={groupId} />
      ))}
    </>
  );
}

function NestingHud() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <ReactPortal wrapperId="nesting-hud-portal">
      {isOpen && (
        <div className={styles.panel}>
          <div className={styles.header}>
            <span className={styles.title}>Nesting HUD</span>
            <button
              className={styles.closeButton}
              onClick={() => setIsOpen(false)}
              aria-label="Close nesting HUD"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M1 1l12 12M13 1L1 13" />
              </svg>
            </button>
          </div>
          <div className={styles.body}>
            <PanelBody />
          </div>
        </div>
      )}

      <button
        className={cn(styles.trigger, { [styles.open]: isOpen })}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Toggle nesting HUD"
        title="Nesting HUD"
      >
        <svg
          className={styles.triggerIcon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
        </svg>
      </button>
    </ReactPortal>
  );
}

export default NestingHud;
